/**
 * Derived audio staleness (story-entities-redesign §2).
 *
 * Staleness is COMPUTED, never synced:
 *   audio stale ⇔ textBlock.contentVersion > coalesce(audioBlock.sourceVersion, 0)
 *
 * `audio_story_blocks.is_stale` is still WRITTEN for worker/legacy compat,
 * but portal code must not READ it — use these helpers instead.
 */

import type { AudioBlock } from '../types';

type StaleInput = Pick<AudioBlock, 'audioUrl' | 'sourceVersion'>;
type PendingInput = Pick<AudioBlock, 'audioUrl' | 'generationStatus'>;

/**
 * Audio exists but was generated from an older text version.
 * `sourceVersion` NULL/0 counts as "older than any real version" (legacy backfill).
 */
export function isAudioBlockStale(
  textBlockContentVersion: number | null | undefined,
  audioBlock: StaleInput | null | undefined,
): boolean {
  if (!audioBlock?.audioUrl) return false;
  if (textBlockContentVersion == null) return false;
  return textBlockContentVersion > (audioBlock.sourceVersion ?? 0);
}

/**
 * Block has no audio yet and generation has not terminally failed —
 * i.e. it is idle-precreated, queued or generating. Replaces the old
 * `!audioUrl && !isStale` heuristic.
 */
export function isAudioBlockPending(audioBlock: PendingInput): boolean {
  return !audioBlock.audioUrl && audioBlock.generationStatus !== 'failed';
}
